import { useState, useCallback } from "react";
import { TopBar } from "../components/layout/TopBar";
import { SectionLabel } from "../components/shared/SectionLabel";
import { EmptyState } from "../components/shared/EmptyState";
import { Spinner } from "../components/shared/Spinner";
import * as ui from "../styles/ui";
import { api, type ReviewRow, type ReviewState } from "../api/client";

const STATES: Array<ReviewState | "all"> = [
  "all",
  "assigned",
  "signed",
  "rejected",
];

export function ReviewQueuePage() {
  const [reviewer, setReviewer] = useState("");
  const [state, setState] = useState<ReviewState | "all">("all");
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.reviews.queue(
        reviewer.trim() || undefined,
        state === "all" ? undefined : state,
      );
      setRows(res.reviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load queue");
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [reviewer, state]);

  const act = useCallback(async (docId: string, action: "sign" | "reject") => {
    setError(null);
    try {
      const res =
        action === "sign"
          ? await api.reviews.sign(docId)
          : await api.reviews.reject(docId);
      // Patch the affected rows in place from the server response.
      setRows((prev) =>
        (prev ?? []).map((r) => {
          const updated = res.reviews.find(
            (u) => u.doc_id === r.doc_id && u.reviewer === r.reviewer,
          );
          return updated ?? r;
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action}`);
    }
  }, []);

  return (
    <>
      <TopBar title="review queue" />
      <div style={ui.pageContent}>
        <SectionLabel>queue</SectionLabel>
        <div style={styles.row}>
          <input
            type="text"
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            placeholder="id"
            data-testid="reviewer-input"
            style={styles.input}
          />
          <div style={styles.modeRow}>
            {STATES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setState(s)}
                style={{
                  ...styles.modeBtn,
                  ...(state === s ? styles.modeBtnActive : {}),
                }}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={styles.btn}
          >
            {loading ? "loading…" : "load"}
          </button>
        </div>

        {error && (
          <div style={styles.error} data-testid="review-error">
            {error}
          </div>
        )}
        {loading && <Spinner size={22} label="loading…" />}

        {!loading && rows && rows.length === 0 && (
          <EmptyState title="empty queue" body="no review assignments match" />
        )}

        {!loading && rows && rows.length > 0 && (
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={ui.th}>document</th>
                <th style={{ ...ui.th, width: 120 }}>reviewer</th>
                <th style={{ ...ui.th, width: 90 }}>state</th>
                <th style={{ ...ui.th, width: 160 }}>assigned</th>
                <th style={{ ...ui.th, width: 140 }}>actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.doc_id}:${r.reviewer}`} style={ui.row}>
                  <td style={styles.mono}>{r.doc_id}</td>
                  <td style={styles.mono}>{r.reviewer}</td>
                  <td style={styles.mono}>
                    <span style={stateStyle(r.state)}>{r.state}</span>
                  </td>
                  <td style={styles.mono}>{r.assigned_at}</td>
                  <td style={ui.td}>
                    {r.state === "assigned" ? (
                      <div style={styles.actions}>
                        <button
                          type="button"
                          onClick={() => act(r.doc_id, "sign")}
                          style={styles.signBtn}
                        >
                          sign off
                        </button>
                        <button
                          type="button"
                          onClick={() => act(r.doc_id, "reject")}
                          style={styles.rejectBtn}
                        >
                          reject
                        </button>
                      </div>
                    ) : (
                      <span style={styles.muted}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function stateStyle(state: ReviewState): React.CSSProperties {
  if (state === "signed") return { color: "var(--fg-1)", fontWeight: 600 };
  if (state === "rejected")
    return { color: "var(--comp-salmon)", fontWeight: 600 };
  return { color: "var(--fg-3)" };
}

const styles: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "8px 0 16px",
    flexWrap: "wrap" as const,
  },
  input: {
    flex: 1,
    minWidth: 180,
    border: "1px solid var(--comp-rule)",
    padding: "5px 10px",
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--fg-1)",
    background: "transparent",
    outline: "none",
  },
  modeRow: { display: "flex", gap: 1, border: "1px solid var(--comp-rule)" },
  modeBtn: {
    padding: "5px 8px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  modeBtnActive: {
    color: "var(--fg-1)",
    fontWeight: 600,
    background: "var(--comp-hover)",
  },
  btn: {
    padding: "5px 12px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "var(--comp-ink)",
    color: "var(--adj-white)",
    border: "1px solid var(--comp-ink)",
  },
  error: {
    color: "var(--comp-salmon)",
    fontSize: 12,
    marginBottom: 10,
    fontFamily: "var(--font-mono)",
  },
  mono: {
    padding: "var(--row-pad)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
  },
  actions: { display: "flex", gap: 6 },
  signBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "var(--comp-ink)",
    color: "var(--adj-white)",
    border: "1px solid var(--comp-ink)",
    padding: "2px 8px",
  },
  rejectBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "transparent",
    color: "var(--comp-salmon)",
    border: "1px solid var(--comp-rule)",
    padding: "2px 8px",
  },
  muted: { color: "var(--fg-mute)" },
};
